import { BaseSyntheticEvent, useEffect, useMemo, useState } from "react"

// ** Third Party Components
import axios from "axios"
import { parseCookies } from "nookies"
import { GroupIcon } from "@radix-ui/react-icons"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import { useMutation, useQuery } from "@tanstack/react-query"
import {
  BadgeInfoIcon,
  Bell,
  Check,
  Loader,
  Mail,
  MessageCircle,
  Phone,
  UserCog2,
  X,
} from "lucide-react"

// ** Constants
import { DictType } from "@pma/types"
import { ACCESS_TOKEN } from "@pma/utils/constants"
import {
  EDIT_USER_ENDPOINT,
  EDIT_USER_ORG_PARAMETER_ENDPOINT,
  SERVER_URL,
  USERS_BY_ID_ENDPOINT,
} from "@pma/utils/api-routes"

// ** Stores
import { useAuth } from "@stores/auth-store"

// ** Helpers
import { cn } from "@pma/utils/helpers"

// ** Theme Components
import { Label } from "@components/common/label"
import { Badge } from "@components/common/badge"
import PmsLoader from "@components/common/loader"
import { Button } from "@components/common/button"
import { Separator } from "@components/common/separator"
import { Avatar, AvatarFallback, AvatarImage } from "@components/common/avatar"
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/common/tooltip"
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@components/common/card"

// ** Custom Hooks
import { toast } from "@pma/hooks/use-toast"
import useTranslator from "@hooks/use-translator"

// ** Custom Components
import UserEditor from "./user-editor"
import Recaptcha from "../auth/recaptcha"
import UserAvatar from "@components/users/user-avatar"
import UpdatePasswordModal from "@components/users/update-password"
import RemoveUserFromOrganization from "./remove-user-from-organization"
import TransferAccessModal from "./transfer-access-modal"
import { Switch } from "../common/switch"
import { USERS_PAGE_ROUTE } from "@pma/utils/page-routes"

interface Props {
  userId: string
}

const UserProfile = ({ userId }: Props) => {
  const [searchParam] = useSearchParams()
  const { authConfig, signIn } = useAuth()
  const { translate, locale } = useTranslator()
  const navigate = useNavigate()

  const isHebrew = locale === "he"
  const authUserId = authConfig?.id
  const orgId = authConfig?.last_organization_id
  const accessToken = parseCookies()[ACCESS_TOKEN]
  const passwordExpired = searchParam.get("password_expired")

  const [v2Fallback, setV2Fallback] = useState(false)
  const [showLoader, setShowLoader] = useState("")
  const [recaptchaToken, setRecaptchaToken] = useState<DictType>({ v2: "", v3: "" })
  const [checkboxValues, setCheckBox] = useState({ recive_emails: false, recive_whatsapp: false })
  const [pinSwitch, setPinSwitch] = useState({ pin_project: false, pin_task: false })

  const [changesHappened, setChangesHappened] = useState({
    recive_emails: false,
    recive_whatsapp: false,
  })
  const [openTransferModal, setOpenTransferModal] = useState(false)

  const handleChange = (e: BaseSyntheticEvent) => {
    const { name, checked } = e.target
    setCheckBox((prev) => ({ ...prev, [name]: checked }))
    setChangesHappened((prev) => ({ ...prev, [name]: true }))
  }

  const getUser = async () => {
    const response = await axios.get(
      SERVER_URL + USERS_BY_ID_ENDPOINT + `?user_id=${userId}&organization_id=${orgId}`,
      { headers: { Authorization: `Bearer ${accessToken}` } },
    )

    if (response?.data?.recive_whatsapp.toString()) {
      setCheckBox({
        recive_emails: response?.data?.recive_emails,
        recive_whatsapp: response?.data?.recive_whatsapp,
      })
    }

    if (response?.data?._pin_projects.toString() || response?.data?._pin_tasks.toString()) {
      setPinSwitch({
        pin_project: response?.data?._pin_projects,
        pin_task: response?.data?._pin_tasks,
      })
    }

    if (isLoggedInUser) signIn({ ...response?.data })

    return response.data
  }

  const updateNotificationSettings = async () => {
    const payload = {
      ["selected_languag"]: locale,
      ["recive_whatsapp"]: checkboxValues.recive_whatsapp,
      ["recive_emails"]: checkboxValues.recive_emails,
      recaptcha_response: !v2Fallback ? recaptchaToken.v3 : undefined,
      recaptcha_response_v2: recaptchaToken.v2,
    }

    if (!v2Fallback && !recaptchaToken.v3) {
      setV2Fallback(true)
      return toast({
        title: translate("Info"),
        description: translate("Please complete the reCAPTCHA"),
        variant: "info",
      })
    }

    if ((v2Fallback && !recaptchaToken.v2) || !recaptchaToken.v3) {
      return
    }

    await axios
      .post(SERVER_URL + EDIT_USER_ENDPOINT, payload, {
        headers: { Authorization: `Bearer ${accessToken}` },
      })
      .then((res) => {
        const { data, status } = res

        if (status === 200) {
          refetch()
          setChangesHappened({
            recive_emails: false,
            recive_whatsapp: false,
          })
          setV2Fallback(false)
          toast({
            title: translate("Success"),
            description: translate("Notification settings updated successfully"),
            variant: "success",
          })
        }
      })
      .catch((error) => {
        const errorMessage = error?.response?.data?.message

        if (errorMessage.includes("Invalid Captcha")) setV2Fallback(true)

        toast({
          title: translate("Error"),
          description: errorMessage || translate("Error occurred while updating Notification settings"),
          variant: "error",
        })
      })
  }

  const updateUserDetails = async () => {
    const response = await axios.post(
      SERVER_URL + EDIT_USER_ORG_PARAMETER_ENDPOINT,
      {
        user_id: data?.id,
        recive_whatsapp: checkboxValues?.recive_whatsapp,
        recive_emails: checkboxValues?.recive_emails,
        pin_projects: pinSwitch?.pin_project,
        pin_tasks: pinSwitch?.pin_task,
        organization_id: orgId,
      },
      { headers: { Authorization: `Bearer ${accessToken}` } },
    )

    return response.data
  }

  const pinProjectsAndTasks = async (payload: any) => {
    const response = await axios.post(
      SERVER_URL + EDIT_USER_ORG_PARAMETER_ENDPOINT,
      {
        user_id: data?.id,
        organization_id: orgId,
        recive_whatsapp: checkboxValues?.recive_whatsapp,
        recive_emails: checkboxValues?.recive_emails,
        ...payload,
      },
      { headers: { Authorization: `Bearer ${accessToken}` } },
    )

    return response.data
  }

  const updatePinProjectsAndTasksMutation = useMutation({
    mutationKey: [`roles`],
    mutationFn: (data: any) => pinProjectsAndTasks(data),
    onSuccess: (data) => {
      toast({
        title: translate("Success"),
        description: translate("User Settings updated successfully"),
        variant: "success",
      })
    },
    onError: (error: DictType) => {
      if (showLoader == "project")
        setPinSwitch((prev) => ({ ...prev, pin_project: !prev.pin_project }))
      else if (showLoader == "task") setPinSwitch((prev) => ({ ...prev, pin_task: !prev.pin_task }))

      toast({
        title: translate("Error"),
        description: translate(error?.response?.data.message) ||
                translate("Error occurred while updating") + " " + translate("role"),
        variant: "error",
      })
    },
  })

  const updateUserNotificationMutation = useMutation({
    mutationKey: [`roles`],
    mutationFn: () => updateUserDetails(),
    onSuccess: (data) => refetch(),
    onError: (error: DictType) => {
      toast({
        title: translate("Error"),
        description: translate(error?.response?.data.message) ||
                translate("Error occurred while updating") + " " + translate("role"),
        variant: "error",
      })
    },
  })

  const handleCancel = () => {
    if (data) {
      setCheckBox({ recive_emails: data?.recive_emails, recive_whatsapp: data?.recive_whatsapp })
      setChangesHappened({
        recive_emails: false,
        recive_whatsapp: false,
      })
    }
  }

  const { data, refetch, isLoading, error, isError } = useQuery({
    queryKey: ["user", userId],
    queryFn: getUser,
    enabled: !passwordExpired,
  })

  useEffect(() => {
    if (!isLoading && !data) navigate(USERS_PAGE_ROUTE)
  }, [isLoading])

  const isLoggedInUser = useMemo(() => authUserId === data?.id, [data?.id])

  let responseError: DictType = {}

  if (isError) {
    responseError = error as DictType
  }

  useEffect(() => {
    if (checkboxValues && data) {
      if (
        checkboxValues.recive_whatsapp === data?.recive_whatsapp &&
        checkboxValues.recive_emails === data?.recive_emails
      ) {
        setChangesHappened({
          recive_emails: false,
          recive_whatsapp: false,
        })
      }
    }
  }, [checkboxValues])

  return (
    <Card className='my-6 min-h-[79vh] w-full space-y-3 border-none shadow-none'>
      <CardHeader>
        <CardTitle
          style={{ textAlign: isHebrew ? "right" : "left" }}
          className='text-xl font-semibold'
        >
          {translate("User Profile")}
        </CardTitle>
      </CardHeader>
      {isLoading ? (
        <PmsLoader />
      ) : (
        <Card className='grid w-full grid-cols-2 items-center justify-around gap-4 p-2 md:p-4'>
          {v2Fallback && (
            <small
              className={cn(
                "fixed top-[100px] text-red-500 transition-all ease-in-out",
                isHebrew ? "left-5" : "right-5",
              )}
            >
              {translate("Please complete")} Recaptcha {translate("and try again")}
            </small>
          )}
          {/* GOOGLE Recaptcha */}
          <Recaptcha
            className={`fixed top-[158px] ${isHebrew ? "ps-5" : "pe-5"}`}
            setToken={setRecaptchaToken}
            v2={v2Fallback}
            reSubmit={updateNotificationSettings}
          />
          <CardContent className='col-span-2 min-h-fit w-full flex-1 md:h-[300px]'>
            <div className='flex flex-col items-center gap-3 p-4 md:flex-row'>
              <UserAvatar user={data} refetch={refetch} />
              <div className='mb-2 flex w-fit flex-col gap-2 text-center'>
                <UserEditor
                  id={data?.id}
                  title='name'
                  value={data?.name}
                  task={data}
                  refetch={refetch}
                  v2Fallback={v2Fallback}
                  setV2Fallback={setV2Fallback}
                  recaptchaToken={recaptchaToken}
                >
                  <h1 className='text-3xl font-bold'> {data?.name ? data?.name : "-"}</h1>
                </UserEditor>

                <UserEditor
                  id={data?.id}
                  title='job_title'
                  value={data?.job_title?.name}
                  task={data}
                  refetch={refetch}
                  v2Fallback={v2Fallback}
                  setV2Fallback={setV2Fallback}
                  recaptchaToken={recaptchaToken}
                >
                  <p className='text-sm text-muted-foreground'>{data?.job_title?.name}</p>
                </UserEditor>
                <div className='mb-2 flex w-auto justify-start'>
                  <UserEditor
                    id={data?.id}
                    title='status'
                    value={data?.status?.name}
                    task={data}
                    refetch={refetch}
                    v2Fallback={v2Fallback}
                    setV2Fallback={setV2Fallback}
                    recaptchaToken={recaptchaToken}
                  >
                    <Badge variant={"outline"} className='w-fit text-black'>
                      {translate(data?.status?.name) || "---"}
                    </Badge>
                  </UserEditor>
                  <UserEditor
                    id={data?.id}
                    title='role'
                    value={data?.role?.name}
                    task={data}
                    refetch={refetch}
                    v2Fallback={v2Fallback}
                    setV2Fallback={setV2Fallback}
                    recaptchaToken={recaptchaToken}
                  >
                    <Badge className='w-fit text-white'>
                      {translate(data?.role?.name) || "---"}
                    </Badge>
                  </UserEditor>
                </div>
                <UpdatePasswordModal user={data} />

                <div className='flex w-fit'>
                  {!isLoggedInUser && (
                    <Link
                      to={`https://wa.me/${data?.phone_number}`}
                      target='_blank'
                      className='mt-2'
                    >
                      <Button
                        variant={"secondary"}
                        size={"sm"}
                        leftSection={<MessageCircle size={14} />}
                        className='text-xs'
                      >
                        Whatsapp
                      </Button>
                    </Link>
                  )}
                </div>
              </div>
              {responseError?.response?.data?.message && (
                <CardContent className='flex flex-col items-center justify-center'>
                  <BadgeInfoIcon
                    className='text-[400px] text-destructive'
                    scale={200}
                    fontSize={"60px"}
                  />
                  <h3 className='text-lg font-semibold text-muted-foreground'>
                    {translate(responseError?.response?.data?.message)}
                  </h3>
                </CardContent>
              )}
              <div className='flex w-full flex-1 flex-col space-y-4 md:items-end'>
                <div className='flex w-[200px] flex-col justify-start gap-1'>
                  <Label className='flex items-center gap-2 font-semibold'>
                    {translate("Email")}

                    <Mail size={14} />
                  </Label>
                  <p className='text-muted-foreground hover:text-slate-500'>{data?.email}</p>
                </div>

                <div className='flex w-[200px] flex-col justify-start gap-1'>
                  <Label className='flex items-center gap-2 font-semibold'>
                    {translate("Phone")}
                    <Phone size={14} />
                  </Label>
                  <UserEditor
                    id={data?.id}
                    title='phone_number'
                    value={data?.phone_number}
                    task={data}
                    refetch={refetch}
                    v2Fallback={v2Fallback}
                    setV2Fallback={setV2Fallback}
                    recaptchaToken={recaptchaToken}
                  >
                    <p className='w-full text-muted-foreground'>
                      {data?.phone_number && data?.phone_number != "" ? data?.phone_number : "---"}
                    </p>
                  </UserEditor>
                </div>
                <div className='flex w-[200px] flex-col justify-start gap-1'>
                  <Label className='flex items-center gap-2 font-semibold'>
                    {translate("Department")}
                    <GroupIcon />
                  </Label>
                  <UserEditor
                    id={data?.id}
                    title='department'
                    value={data?.department?.name}
                    task={data}
                    refetch={refetch}
                    v2Fallback={v2Fallback}
                    setV2Fallback={setV2Fallback}
                    recaptchaToken={recaptchaToken}
                  >
                    <p className='w-full text-muted-foreground'>
                      {data?.department?.name ?? "---"}
                    </p>
                  </UserEditor>
                </div>

                <div className='flex w-[200px] flex-col justify-start gap-1'>
                  <Label className='flex items-center gap-2 font-semibold'>
                    {translate("Manager")}
                    <UserCog2 size={14} />
                  </Label>
                  <UserEditor
                    id={data?.id}
                    title='manager'
                    value={data?.manager?.id}
                    task={data}
                    refetch={refetch}
                    v2Fallback={v2Fallback}
                    setV2Fallback={setV2Fallback}
                    recaptchaToken={recaptchaToken}
                  >
                    <div className='flex w-full items-center gap-2'>
                      <img
                        src={`${data?.manager?.user_picture?.url || process.env.PUBLIC_URL + "/user-profile.svg"}`}
                        className='h-[25px] w-[25px] rounded-full object-cover'
                      />
                      <span className='text-muted-foreground'>{data?.manager?.name ?? "---"}</span>
                    </div>
                  </UserEditor>
                </div>

                <div
                  className={`flex flex-col justify-start gap-1 ${isHebrew ? "w-[208px]" : "w-[200px]"}`}
                >
                  <Label className='flex items-start gap-2 font-semibold'>
                    {translate("Notification settings")}
                    <Bell size={14} />
                  </Label>
                  <div className='flex flex-col ps-1'>
                    <div className='flex h-[32px] flex-row gap-10'>
                      <Tooltip>
                        <TooltipTrigger>
                          <div className='flex gap-2'>
                            <input
                              type='checkbox'
                              name='recive_whatsapp'
                              onChange={handleChange}
                              checked={checkboxValues.recive_whatsapp}
                              disabled={!data?.phone_number}
                            />
                            <Label
                              className={`flex items-center text-left font-semibold ${!data?.phone_number && "pointer-events-none opacity-50"}`}
                            >
                              {translate("Whatsapp notification")}
                            </Label>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent className={`${!data?.phone_number && "border bg-white"}`}>
                          {!data?.phone_number ? (
                            <span className='text-red-500'>
                              {translate("Kindly add mobile number in your profile first")}
                            </span>
                          ) : checkboxValues.recive_whatsapp ? (
                            translate(
                              "Unchecked/disabled this option to stop whatsapp notification",
                            )
                          ) : (
                            translate("Checked/enable this option to start whatsapp notification")
                          )}
                        </TooltipContent>
                      </Tooltip>
                    </div>
                    <div className={`flex h-[32px] flex-row gap-10`}>
                      <Tooltip>
                        <TooltipTrigger>
                          <div className='flex gap-2'>
                            <input
                              type='checkbox'
                              name='recive_emails'
                              onChange={handleChange}
                              checked={checkboxValues.recive_emails}
                            />
                            <Label className='flex items-center text-left font-semibold'>
                              {translate("Email notification")}
                            </Label>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>
                          {checkboxValues.recive_emails
                            ? translate("Unchecked/disabled this option to stop email notification")
                            : translate("Checked/enable this option to start email notification")}
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  </div>{" "}
                  {(changesHappened.recive_whatsapp || changesHappened.recive_emails) && (
                    <div className='flex flex-row gap-2'>
                      <Tooltip>
                        <TooltipTrigger>
                          <Button
                            className='h-9 w-10 px-1 py-1'
                            variant='outline'
                            onClick={handleCancel}
                          >
                            <X size={"14"} />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          {translate("Click this button to cancel this changes")}
                        </TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger>
                          <Button
                            className='h-9 w-10 bg-primary px-1 py-1'
                            variant={"default"}
                            onClick={() => updateUserNotificationMutation.mutate()}
                          >
                            <Check size={"14"} />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          {translate("Click this button to save this changes")}
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  )}
                  <div className='flex flex-col ps-1'>
                    <div className='flex flex-col gap-2'>
                      <label className='font-[inter] text-sm font-semibold'>
                        {translate("Auto Pin New Projects")}
                      </label>
                      <div className='flex gap-2'>
                        <Switch
                          className='h-6 w-11'
                          checked={pinSwitch.pin_project}
                          disabled={
                            updatePinProjectsAndTasksMutation.isPending && showLoader === "project"
                          }
                          onCheckedChange={(checked) => {
                            setShowLoader("project")
                            setPinSwitch((prev) => ({ ...prev, pin_project: checked }))
                            updatePinProjectsAndTasksMutation.mutate({
                              pin_projects: checked,
                              pin_tasks: pinSwitch.pin_task,
                            })
                          }}
                        />
                        {updatePinProjectsAndTasksMutation.isPending && showLoader === "project" ? (
                          <Loader className='animate-spin' size={20} />
                        ) : null}
                      </div>
                    </div>
                    <div className='flex flex-col gap-2'>
                      <label className='font-[inter] text-sm font-semibold'>
                        {translate("Auto Pin New Tasks")}
                      </label>
                      <div className='flex gap-2'>
                        <Switch
                          className='h-6 w-11'
                          checked={pinSwitch.pin_task}
                          disabled={
                            updatePinProjectsAndTasksMutation.isPending && showLoader === "task"
                          }
                          onCheckedChange={(checked) => {
                            setShowLoader("task")
                            setPinSwitch((prev) => ({ ...prev, pin_task: checked }))
                            updatePinProjectsAndTasksMutation.mutate({
                              pin_projects: pinSwitch.pin_project,
                              pin_tasks: checked,
                            })
                          }}
                        />
                        {updatePinProjectsAndTasksMutation.isPending && showLoader === "task" ? (
                          <Loader className='animate-spin' size={20} />
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>

                <Button
                  className='mt-3 bg-primary text-white'
                  onClick={() => setOpenTransferModal(true)}
                  style={{
                    width: "200px",
                  }}
                >
                  {translate("Transfer Access & Roles")}
                </Button>
              </div>
            </div>
          </CardContent>

          <Separator className='col-span-2' />

          {openTransferModal && (
            <TransferAccessModal
              open={openTransferModal}
              onClose={() => setOpenTransferModal(false)}
              sourceUser={data}
            />
          )}

          <CardFooter className='col-span-2 flex flex-col p-0'>
            {!isLoggedInUser && (
              <fieldset className='flex h-20 w-full items-center justify-between rounded-md border border-destructive/20 p-2'>
                <legend className='text-sm font-semibold text-destructive/70'>
                  {translate("Danger zone")}
                </legend>
                <span className='text-sm'>
                  {translate("Remove")} {data?.name} {translate("from organization")}
                </span>
                <RemoveUserFromOrganization user={data} />
              </fieldset>
            )}
          </CardFooter>
        </Card>
      )}
    </Card>
  )
}

export default UserProfile
